import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRocket,
  faSpinner,
  faCheck,
  faUpload,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { useAccount } from '../contexts/AccountContext';
import { useEnabledChannels } from '../hooks/useEnabledChannels';
import { useAccountSettings } from '../hooks/useAccountSettings';
import { getUserData } from '../utils/userUtils';
import { hasChannelAccess, isSuperAdmin } from '../utils/permissions';
import { getEnabledPlatforms } from '../utils/accountSettingPlatformList';
import './CampaignCreation.css';
import './MediaPlanCreation.css';

// Channel definitions
const CHANNEL_DEFINITIONS = [
  { id: 'facebook', name: 'Facebook', icon: 'facebook' },
  { id: 'google-ads-search', name: 'Google Search', icon: 'search' },
  { id: 'google-ads-pmax', name: 'Google PMax', icon: 'ads_click' },
  { id: 'google-ads-demandgen', name: 'Google DemandGen', icon: 'auto_awesome' },
  { id: 'dv360', name: 'DV360', icon: 'smart_display' },
  { id: 'youtube-dv360', name: 'YouTube DV360', icon: 'play_circle' },
  { id: 'ttd', name: 'The Trade Desk', icon: 'ads_click' },
  { id: 'tiktok', name: 'TikTok', icon: 'music_note' },
  { id: 'linkedin', name: 'LinkedIn', icon: 'work' },
  { id: 'reddit', name: 'Reddit', icon: 'forum' }
];

/**
 * MediaPlanCreation component for creating new media plans
 * Aligned with PacingCreation patterns
 */
const MediaPlanCreation = () => {
  const navigate = useNavigate();
  const { getAccountIdToUse } = useAccount();
  const [user] = useState(() => getUserData());
  const accountId = getAccountIdToUse();
  const { enabledChannels, isLoading: channelsLoading } = useEnabledChannels(accountId, user);
  const { settings: accountSettings } = useAccountSettings(accountId, user);

  // Get media-planner-enabled platforms, canonicalized to channel ids so any
  // accepted alias works (see accountSettingPlatformList).
  const mediaPlannerPlatforms = getEnabledPlatforms(accountSettings?.['media-planner-enabled']);

  // Form state
  const [planName, setPlanName] = useState('');
  const [campaignBrief, setCampaignBrief] = useState('');
  const [budget, setBudget] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [referenceFile, setReferenceFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Check channel access
  const userHasChannelAccess = (channelId) => {
    if (isSuperAdmin(user)) return true;
    return hasChannelAccess(user, channelId);
  };

  // Available channels based on account settings, enabled channels, and user permissions
  const availableChannels = useMemo(() =>
    CHANNEL_DEFINITIONS.filter(channel =>
      enabledChannels?.[channel.id] &&
      userHasChannelAccess(channel.id) &&
      mediaPlannerPlatforms.includes(channel.id)
    ),
    [enabledChannels, mediaPlannerPlatforms]
  );

  // Track if we've done initial auto-selection
  const hasAutoSelected = useRef(false);

  // Auto-select all channels on initial load only
  useEffect(() => {
    if (availableChannels.length > 0 && !hasAutoSelected.current) {
      hasAutoSelected.current = true;
      setSelectedChannels(availableChannels.map(ch => ch.id));
    }
  }, [availableChannels]);

  // Toggle channel selection
  const handleChannelToggle = (channelId) => {
    setSelectedChannels(prev =>
      prev.includes(channelId)
        ? prev.filter(id => id !== channelId)
        : [...prev, channelId]
    );
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReferenceFile(file);
    }
  };

  // Remove uploaded file
  const handleRemoveFile = () => {
    setReferenceFile(null);
  };

  // Validate form
  const isFormValid = () => {
    return planName.trim() && campaignBrief.trim() && budget && startDate && endDate && selectedChannels.length > 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('accountId', accountId);
      formData.append('userId', user?.userId || user?.id);
      formData.append('campaignBrief', campaignBrief);
      formData.append('budget', budget);
      formData.append('startDate', startDate);
      formData.append('endDate', endDate);
      formData.append('channels', JSON.stringify(selectedChannels));
      if (referenceFile) {
        formData.append('referenceFile', referenceFile);
      }

      const response = await api.post(getApiUrl('/api/v2/media-planner/generate'), formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // v1 message-based flow returns chatId and messageId
      if (response.data?.messageId) {
        // Save to database immediately with 'processing' status and navigate to details
        const saveResponse = await api.post(getApiUrl('/api/v2/media-plans'), {
          accountId,
          userId: user?.userId || user?.id,
          name: planName.trim(),
          chatId: response.data.chatId,
          messageId: response.data.messageId,
          campaignBrief,
          totalBudget: parseFloat(budget),
          startDate,
          endDate,
          channels: selectedChannels,
          status: 'processing'
        });

        if (saveResponse.data?.id) {
          // Navigate to details page - it will handle polling via messageId
          navigate(`/media-plans/${saveResponse.data.id}`);
        } else {
          setError('Failed to save media plan');
          setIsSubmitting(false);
        }
      } else {
        setError('Failed to generate media plan: No message ID returned');
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('Error generating media plan:', err);
      setError(err.response?.data?.error || 'Failed to start media plan generation');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="media-plan-creation-container">
      <div className="media-plan-creation-header">
        <h1>Create Media Plan</h1>
      </div>

      {error && (
        <div className="media-plan-creation-error">
          <p>{error}</p>
          <button onClick={() => setError(null)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      <form className="media-plan-creation-form" onSubmit={handleSubmit}>
          {/* Media Plan Name */}
          <div className="form-group">
            <label htmlFor="plan-name">Media Plan Name *</label>
            <input
              type="text"
              id="plan-name"
              className="form-control"
              placeholder="e.g., Q1 2026 Brand Awareness Campaign"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              required
            />
          </div>

          {/* Campaign Brief */}
          <div className="form-group">
            <label htmlFor="campaign-brief">Campaign Brief *</label>
            <p className="form-help-text">
              Describe your campaign goals, target audience, and any specific requirements
            </p>
            <textarea
              id="campaign-brief"
              className="form-control"
              placeholder="e.g., Help me plan a campaign for a new SaaS product launch targeting enterprise decision-makers..."
              value={campaignBrief}
              onChange={(e) => setCampaignBrief(e.target.value)}
              rows={6}
              required
            />
          </div>

          {/* Budget and Dates */}
          <div className="form-row three-col">
            <div className="form-group">
              <label htmlFor="budget">Total Budget ($) *</label>
              <input
                type="number"
                id="budget"
                className="form-control"
                placeholder="e.g., 50000"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                min="1"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="start-date">Start Date *</label>
              <input
                type="date"
                id="start-date"
                className="form-control"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  // Reset end date if it's now before the new start date
                  if (endDate && e.target.value > endDate) {
                    setEndDate('');
                  }
                }}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="end-date">End Date *</label>
              <input
                type="date"
                id="end-date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || new Date().toISOString().split('T')[0]}
                required
              />
            </div>
          </div>

          {/* Channel Selection */}
          <div className="form-group">
            <label>Select Channels *</label>
            <p className="form-help-text">
              Choose the advertising platforms for your campaign
            </p>
            <div className="destination-grid" role="group" aria-label="Channel selection">
              {channelsLoading ? (
                <div className="loading-destinations">
                  <p>Loading available channels...</p>
                </div>
              ) : availableChannels.length === 0 ? (
                <div className="no-destinations">
                  <p>No channels available for media planning.</p>
                  <p>Please contact your administrator to configure enabled platforms.</p>
                </div>
              ) : (
                availableChannels.map(channel => {
                  const isSelected = selectedChannels.includes(channel.id);
                  return (
                    <button
                      key={channel.id}
                      type="button"
                      className={`destination-option ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleChannelToggle(channel.id)}
                      role="checkbox"
                      aria-checked={isSelected}
                    >
                      <span className="material-symbols-outlined destination-icon">
                        {channel.icon}
                      </span>
                      <span className="destination-name">{channel.name}</span>
                      {isSelected && (
                        <FontAwesomeIcon icon={faCheck} className="check-icon" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Reference File Upload (Optional) */}
          <div className="form-group">
            <label>Reference Document (Optional)</label>
            <p className="form-help-text">
              Upload a PDF with additional campaign details, brand guidelines, or previous plans
            </p>
            <div className="file-upload-area">
              <input
                type="file"
                className="file-input"
                accept=".pdf"
                onChange={handleFileUpload}
              />
              <div className="file-upload-content">
                <FontAwesomeIcon icon={faUpload} className="upload-icon" />
                <p>Drag & drop your PDF here, or click to browse</p>
                <p className="file-formats">Supported format: PDF (max 100MB)</p>
              </div>
            </div>

            {referenceFile && (
              <div className="uploaded-files-list">
                <div className="uploaded-file-item">
                  <span className="file-name">{referenceFile.name}</span>
                  <span className="file-size">({(referenceFile.size / 1024 / 1024).toFixed(1)} MB)</span>
                  <button type="button" className="remove-file-btn" onClick={handleRemoveFile}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/media-plans')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isFormValid() || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Generating...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faRocket} />
                  Generate Media Plan
                </>
              )}
            </button>
          </div>
        </form>
    </div>
  );
};

export default MediaPlanCreation;

